import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Phone, Calendar, Car, Wrench, StickyNote } from "lucide-react";
import Logo from "@/components/Logo";
import { SERVICES, API } from "@/data/site";

const inputCls =
  "w-full border border-[#262A35] bg-[#0B0C0E] px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:border-[#3B82F6] focus:outline-none transition-colors";

const serviceName = (id) => SERVICES.find((s) => s.id === id)?.title || (id === "general" ? "General Enquiry" : id);

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("bcc_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBookings = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/bookings`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setBookings(res.data);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem("bcc_admin_token");
          setToken("");
          toast.error("Session expired. Please log in again.");
        } else {
          toast.error("Could not load bookings.");
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) fetchBookings(token);
  }, [token, fetchBookings]);

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("bcc_admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back, admin.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("bcc_admin_token");
    setToken("");
    setBookings([]);
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center px-4 pt-16">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm border border-[#262A35] bg-[#121418] p-8"
        >
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div>
              <p className="font-display text-lg font-black uppercase text-white">Admin Access</p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#3B82F6]">
                Bowegans Car Care
              </p>
            </div>
          </div>
          <div className="mt-8 space-y-4">
            <input
              data-testid="admin-login-email-input"
              type="email"
              className={inputCls}
              placeholder="Admin email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              data-testid="admin-login-password-input"
              type="password"
              className={inputCls}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="submit"
              disabled={loginLoading}
              data-testid="admin-login-submit-button"
              className="font-mono2 w-full bg-[#3B82F6] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#2563EB] disabled:opacity-60"
            >
              {loginLoading ? "Checking..." : "Log In"}
            </button>
          </div>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#3B82F6]">Admin</p>
          <h1 className="font-display mt-1 text-2xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
            Booking Requests
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-refresh-button"
            onClick={() => fetchBookings(token)}
            className="font-mono2 inline-flex items-center gap-2 border border-[#262A35] px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-gray-300 transition-colors hover:border-[#3B82F6] hover:text-white"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="font-mono2 inline-flex items-center gap-2 border border-[#262A35] px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-gray-300 transition-colors hover:border-[#3B82F6] hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </div>

      <div className="mt-8 inline-flex items-center gap-3 border border-[#262A35] bg-[#121418] px-6 py-4" data-testid="admin-bookings-count">
        <span className="font-display text-3xl font-black text-white">{bookings.length}</span>
        <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-gray-500">
          Total requests
        </span>
      </div>

      {loading && bookings.length === 0 ? (
        <p className="font-mono2 mt-10 text-xs uppercase tracking-[0.3em] text-gray-500" data-testid="admin-loading">
          Loading bookings...
        </p>
      ) : bookings.length === 0 ? (
        <p className="font-mono2 mt-10 text-xs uppercase tracking-[0.3em] text-gray-500" data-testid="admin-empty">
          No booking requests yet.
        </p>
      ) : (
        <div className="mt-8 grid gap-5 md:grid-cols-2" data-testid="admin-bookings-list">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-item-${b.id}`} className="spotlight-card border border-[#262A35] bg-[#121418] p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-display text-lg font-bold text-white">{b.name}</p>
                  <p className="font-mono2 mt-1 text-[10px] uppercase tracking-[0.2em] text-[#3B82F6]">
                    {serviceName(b.service)}
                  </p>
                </div>
                <a
                  href={`tel:${b.phone}`}
                  data-testid={`booking-call-${b.id}`}
                  className="font-mono2 inline-flex shrink-0 items-center gap-2 bg-[#3B82F6] px-3.5 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#2563EB]"
                >
                  <Phone className="h-3.5 w-3.5" />
                  {b.phone}
                </a>
              </div>
              <div className="mt-4 space-y-2 text-sm text-gray-400">
                <p className="flex items-center gap-2.5">
                  <Car className="h-4 w-4 shrink-0 text-gray-600" /> {b.vehicle}
                </p>
                {b.date && (
                  <p className="flex items-center gap-2.5">
                    <Calendar className="h-4 w-4 shrink-0 text-gray-600" /> Preferred: {b.date}
                  </p>
                )}
                {b.notes && (
                  <p className="flex items-start gap-2.5">
                    <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-gray-600" /> {b.notes}
                  </p>
                )}
                <p className="flex items-center gap-2.5 text-xs text-gray-600">
                  <Wrench className="h-4 w-4 shrink-0" />
                  Received {new Date(b.created_at).toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
